import { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Activity, AlertOctagon, CheckCircle2, Clock, Play } from "lucide-react";
import { errorText, fmtStamp } from "@/lib/calendar";

const Stat = ({ label, value, tone, testId }) => (
  <div className={`rounded-xl border p-3 ${tone}`} data-testid={testId}>
    <p className="text-xs uppercase tracking-wide font-semibold opacity-70">{label}</p>
    <p className="text-2xl font-black">{value}</p>
  </div>
);

export const ReminderStatusCard = ({ refreshKey = 0 }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    apiClient.get("/reminders/status").then(({ data }) => setSt(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post("/reminders/run-now");
      toast.success(`Reminder check done: ${data.sent} sent, ${data.failed} failed`);
      load();
    } catch (e) {
      toast.error(errorText(e, "Reminder check failed"));
    } finally {
      setBusy(false);
    }
  };

  if (!st) return null;
  const last = st.last_run;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="reminder-status-card">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Activity className="w-5 h-5 text-cyan-600" />Reminder & Notification Health</h3>
        <Button size="sm" onClick={runNow} disabled={busy} variant="outline" data-testid="run-reminders-now-btn"><Play className="w-4 h-4 mr-2" />Run Check Now</Button>
      </div>
      <p className="text-sm text-slate-600" data-testid="reminder-last-run">
        Last check: {last ? `${fmtStamp(last.started_at)} (${last.source}) — ${last.status === "ok" ? "OK" : `FAILED: ${last.error || last.status}`}` : "never"}
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Sent (24h)" value={st.last_24h.sent} tone="border-green-200 bg-green-50 text-green-800" testId="reminder-stat-sent" />
        <Stat label="Failed (24h)" value={st.last_24h.failed} tone="border-red-200 bg-red-50 text-red-800" testId="reminder-stat-failed" />
        <Stat label="Skipped (24h)" value={st.last_24h.skipped} tone="border-slate-200 bg-slate-50 text-slate-700" testId="reminder-stat-skipped" />
        <Stat label="Push failed (24h)" value={st.last_24h.push_failed ?? 0} tone="border-amber-200 bg-amber-50 text-amber-800" testId="reminder-stat-push-failed" />
      </div>
      <div>
        <p className="text-sm font-semibold mb-2 flex items-center gap-2"><AlertOctagon className="w-4 h-4 text-red-500" />Recent failures</p>
        {st.failures.length === 0 ? (
          <p className="text-sm text-green-700 flex items-center gap-2" data-testid="no-failures"><CheckCircle2 className="w-4 h-4" />No failed reminders or notifications</p>
        ) : (
          <div className="space-y-1 max-h-56 overflow-y-auto" data-testid="failure-list">
            {st.failures.map((f) => (
              <p key={f.id} className="text-xs bg-red-50 border border-red-200 rounded-lg p-2">
                <b>{fmtStamp(f.created_at)}</b> · {f.kind} to {f.recipient_role || "user"} {f.recipient_name || ""}
                {f.client_name ? ` (ride: ${f.client_name})` : ""} — {f.status !== "failed" && f.push_status === "failed" ? "in-app delivered, push failed: " : ""}{f.error}
              </p>
            ))}
          </div>
        )}
      </div>
      <div>
        <p className="text-sm font-semibold mb-2 flex items-center gap-2"><Clock className="w-4 h-4 text-cyan-600" />Scheduled in the next 24 hours</p>
        {st.upcoming.length === 0 ? <p className="text-sm text-slate-500">Nothing scheduled.</p> : (
          <div className="space-y-1 max-h-56 overflow-y-auto" data-testid="upcoming-reminders">
            {st.upcoming.map((u, i) => (
              <p key={i} className="text-xs border border-slate-200 rounded-lg p-2">
                {u.send_at} · {u.offset_label} before {u.leg} pickup of <b>{u.client_name}</b> ({u.pickup_at})
                {u.unassigned && <span className="text-red-600 font-bold"> · NO DRIVER</span>}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReminderStatusCard;
